// /admin/porteros/:id — Ficha admin del portero
// Espejo del PorteroDashboard + datos privados de admin (nombres reales, contacto, montos)
// Para gestión: ver vecinos referidos, comisiones pendientes, banco para liquidar.

import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

// QR via api.qrserver.com — público, gratis, sin scripts async ni CSP issues.
// Si en el futuro quieren auto-hosting, instalar npm `qrcode` (ver memoria).
function qrUrlFor(text, sizePx = 600) {
  const q = encodeURIComponent(text)
  // navy oscuro #1A3461 sobre blanco. Si el QR queda "raro" en algunos lectores,
  // usar negro puro (000000) — la mayoría de cámaras lee mejor el contraste alto.
  return `https://api.qrserver.com/v1/create-qr-code/?size=${sizePx}x${sizePx}&color=1A3461&bgcolor=FFFFFF&margin=10&format=png&data=${q}`
}

const NAVY = '#1A3461'
const TEAL = '#00A8B5'
const GREEN = '#15803D'

const fmtUYU = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`
const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-UY', { day:'numeric', month:'short', year:'numeric' }) : '—'
const fmtMes = (d) => d ? new Date(d).toLocaleDateString('es-UY', { month:'long', year:'numeric' }) : '—'
const esc = (s) => String(s || '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]))

const ESTADO_VECINO = {
  activa:    { bg:'rgba(22,163,74,.1)',  color:GREEN,    label:'Activa'    },
  pausada:   { bg:'rgba(245,158,11,.12)', color:'#92400E', label:'Pausada'   },
  cancelada: { bg:'rgba(239,68,68,.12)',  color:'#991B1B', label:'Cancelada' },
}

const ESTADO_COMISION = {
  pendiente:         { bg:'rgba(245,158,11,.12)', color:'#92400E', label:'Pendiente' },
  hold_verificacion: { bg:'rgba(99,102,241,.1)',  color:'#3730A3', label:'En verificación' },
  pagada:            { bg:'rgba(22,163,74,.1)',   color:GREEN,    label:'Pagada' },
  cancelada:         { bg:'rgba(239,68,68,.12)',  color:'#991B1B', label:'Cancelada' },
}

export default function AdminPorteroDetalle() {
  const { id } = useParams()
  const [portero, setPortero] = useState(null)
  const [vecinos, setVecinos] = useState([])
  const [comisiones, setComisiones] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [copiado, setCopiado] = useState(false)
  const [qrReady, setQrReady] = useState(false)

  useEffect(() => { cargar() }, [id])

  async function cargar() {
    setLoading(true); setError(null)
    try {
      const { data: p, error: e1 } = await supabase
        .from('porteros')
        .select(`
          id, slug, lugar, edificio_nombre, direccion, turno, cant_unidades,
          activo, banco_cuenta, notas_pago, created_at, tyc_aceptado_at, tyc_version,
          profile:profiles!porteros_id_fkey(id, nombre, phone, email, role),
          barrio:barrios!porteros_barrio_id_fkey(id, nombre)
        `)
        .eq('id', id).maybeSingle()
      if (e1) throw e1
      if (!p) { setError('Portero no encontrado'); setLoading(false); return }
      setPortero(p)

      // Vecinos referidos — admin SÍ ve todo (titular + dirección + plan + cobro)
      const { data: vec } = await supabase
        .from('clientes')
        .select(`
          id, estado, fecha_inicio, fecha_proximo_cobro, kit_actual,
          direccion, apto, dia_entrega, ventana_horaria,
          titular:profiles!clientes_titular_id_fkey(id, nombre, phone, email)
        `)
        .eq('portero_referido_id', id)
        .order('fecha_inicio', { ascending: false })
      setVecinos(vec || [])

      const { data: com } = await supabase
        .from('comisiones')
        .select('id, mes, monto_total, cantidad_clientes, estado, fecha_pago, referencia_pago, hold_hasta, hold_motivo, notas')
        .eq('portero_id', id)
        .order('mes', { ascending: false })
      setComisiones(com || [])
    } catch (err) {
      console.error(err); setError(err.message || 'Error cargando datos')
    } finally {
      setLoading(false)
    }
  }

  const refUrl = portero?.slug ? `https://limpack.uy/?ref=${portero.slug}` : null
  const nombrePortero = portero?.profile?.nombre || 'el portero'

  // URL del QR (display + impresión). Lo precalculo memoizado.
  const qrUrlDisplay = useMemo(() => refUrl ? qrUrlFor(refUrl, 240) : null, [refUrl])
  const qrUrlPrint   = useMemo(() => refUrl ? qrUrlFor(refUrl, 800) : null, [refUrl])

  // Nombre limpio del portero para el volante. Saca "(test portero)" u otros paréntesis.
  // Si no hay nombre cargado, devuelve null y el volante usa un copy genérico.
  const nombrePort = useMemo(() => {
    const raw = portero?.profile?.nombre
    if (!raw) return null
    return raw.replace(/\s*\(.*?\)\s*/g, '').trim() || null
  }, [portero])
  // Primer nombre solo, para "Soy Carlos" en lugar de "Soy Carlos Báez González" (queda más cálido + entra mejor en folleto chico)
  const primerNombre = useMemo(() => (nombrePort?.split(/\s+/)[0]) || null, [nombrePort])

  function copiarLink() {
    if (!refUrl) return
    navigator.clipboard.writeText(refUrl)
    setCopiado(true)
    setTimeout(() => setCopiado(false), 1800)
  }

  function descargarQR() {
    if (!qrUrlPrint) return
    // Abro en pestaña nueva para que el user haga "Guardar imagen como..."
    // (download directo cross-origin a veces lo bloquea Chrome).
    window.open(qrUrlPrint, '_blank')
  }

  // Mensaje pre-armado para mandarle al portero por WhatsApp con su link.
  function buildWhatsAppParaPortero() {
    return [
      `¡Hola ${nombrePortero}! 👋`,
      '',
      'Te paso tu link y QR personalizados de Limpack:',
      refUrl,
      '',
      'Cualquier vecino que se sume con ese link queda atribuido a vos automáticamente.',
      'Si querés también te dejo el cartel A4 imprimible y el folleto para repartir.',
      '',
      '— Limpack',
    ].join('\n')
  }

  function compartirPorWA() {
    const tel = String(portero?.profile?.phone || '').replace(/\D/g, '')
    const text = encodeURIComponent(buildWhatsAppParaPortero())
    const url = tel ? `https://wa.me/${tel}?text=${text}` : `https://wa.me/?text=${text}`
    window.open(url, '_blank')
  }

  // Helper: imprime un HTML usando un iframe oculto (evita pop-up blockers).
  // Si querés ver antes de imprimir, abre además una pestaña preview.
  function imprimirHTML(html, filename = 'limpack-cartel') {
    // 1) Pestaña preview (opcional pero útil para chequear el diseño)
    try {
      const blob = new Blob([html], { type: 'text/html' })
      const url = URL.createObjectURL(blob)
      const tab = window.open(url, '_blank')
      if (!tab) {
        // Fallback si el pop-up está bloqueado: descargar el HTML
        const a = document.createElement('a')
        a.href = url
        a.download = `${filename}.html`
        a.click()
        setTimeout(() => URL.revokeObjectURL(url), 5000)
        return
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (e) {
      console.error('preview falló, sigo a iframe print:', e)
    }

    // 2) iframe oculto que dispara print directo (por si el usuario no abre la pestaña)
    const iframe = document.createElement('iframe')
    Object.assign(iframe.style, {
      position: 'fixed', right: '0', bottom: '0',
      width: '0', height: '0', border: '0', visibility: 'hidden',
    })
    document.body.appendChild(iframe)
    const doc = iframe.contentWindow.document
    doc.open(); doc.write(html); doc.close()
    iframe.onload = () => {
      setTimeout(() => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print() }
        catch (e) { console.error('print iframe:', e) }
        setTimeout(() => { try { document.body.removeChild(iframe) } catch {} }, 2000)
      }, 300)
    }
  }

  // Cartel A4 — un solo QR grande para colgar en el palier del edificio.
  function imprimirCartelA4() {
    if (!qrUrlPrint) return
    const dataUrl = qrUrlPrint
    const edificio = portero?.edificio_nombre || portero?.lugar || ''
    const logoUrl = `${window.location.origin}/logo-vertical.png`
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Cartel Limpack — ${portero?.slug || ''}</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; }
  body { font-family: 'Inter', system-ui, -apple-system, sans-serif; color: #1A3461; background: #E2E8F0; padding: 24px 0; }
  .sheet { width: 210mm; min-height: 297mm; padding: 22mm 20mm; margin: 0 auto; background: #fff; box-shadow: 0 8px 32px rgba(0,0,0,0.15); display: flex; flex-direction: column; justify-content: space-between; }
  .top { text-align: center; }
  .logo { height: 28mm; width: auto; display: block; margin: 0 auto; }
  .tagline { font-size: 18px; color: #00A8B5; font-weight: 700; margin: 6px 0 0; }
  .tag-portero { display: inline-block; background: #1A3461; color: #fff; font-size: 13px; font-weight: 700; letter-spacing: 1px; padding: 6px 16px; border-radius: 999px; margin: 18px 0 14px; text-transform: uppercase; }
  .saludo { font-size: 22px; color: #1A3461; font-weight: 600; margin: 0 0 6px; }
  .hook { font-size: 34px; font-weight: 900; color: #1A3461; margin: 8px 0 0; line-height: 1.15; }
  .sub { font-size: 16px; color: #475569; margin: 14px 0 0; line-height: 1.45; }
  .qr-wrap { text-align: center; margin: 22px 0 0; }
  .qr-wrap img { width: 340px; max-width: 75%; border: 10px solid #1A3461; border-radius: 18px; background: #fff; display: inline-block; }
  .cta { font-size: 24px; font-weight: 800; color: #1A3461; margin: 14px 0 2px; }
  .firma { font-size: 14px; color: #64748B; margin-top: 4px; }
  .firma strong { color: #00A8B5; font-weight: 700; }
  .benefits { display: flex; justify-content: space-around; padding: 16px 0 0; border-top: 2px solid #E2E8F0; gap: 12px; }
  .b { text-align: center; flex: 1; }
  .b-ico { font-size: 26px; line-height: 1; }
  .b-t { font-size: 13.5px; font-weight: 700; color: #1A3461; margin: 4px 0 2px; }
  .b-d { font-size: 11px; color: #64748B; line-height: 1.3; }
  .url { font-size: 10.5px; color: #94A3B8; text-align: center; margin: 10px 0 0; font-family: ui-monospace, monospace; }
  @page { size: A4; margin: 0; }
  @media print {
    body { background: #fff; padding: 0; }
    .sheet { box-shadow: none; width: 210mm; height: 297mm; }
  }
</style></head><body>
<div class="sheet">
  <div class="top">
    <img class="logo" src="${logoUrl}" alt="Limpack" />
    <div class="tag-portero">De tu portero${primerNombre ? `, para vos` : ''}</div>
    <div class="saludo">${primerNombre ? `Hola vecino, soy ${esc(primerNombre)}.` : 'Hola vecino.'}</div>
    <div class="hook">Tu casa limpia,<br/>sin pensarlo.</div>
    <div class="sub">Suscribite al kit mensual o comprá en la tienda.<br/>Te lo dejo yo${edificio ? ` acá en ${esc(edificio)}` : ' en tu puerta'}.</div>
  </div>
  <div class="qr-wrap">
    <img src="${dataUrl}" alt="QR Limpack" />
    <div class="cta">Escaneá y enterate</div>
    <div class="firma">${primerNombre ? `Cualquier cosa me preguntás · <strong>${esc(primerNombre)}</strong>` : 'Cualquier cosa preguntale a tu portero'}</div>
  </div>
  <div>
    <div class="benefits">
      <div class="b"><div class="b-ico">🧴</div><div class="b-t">Kit mensual</div><div class="b-d">Lo que usás todos los meses.</div></div>
      <div class="b"><div class="b-ico">🛒</div><div class="b-t">La tienda</div><div class="b-d">Productos chequeados, buen precio.</div></div>
      <div class="b"><div class="b-ico">💸</div><div class="b-t">Mejor precio</div><div class="b-d">Promedio del mercado.</div></div>
      <div class="b"><div class="b-ico">🚫</div><div class="b-t">Sin contratos</div><div class="b-d">Cancelás cuando quieras.</div></div>
    </div>
    <div class="url">${esc(refUrl)}</div>
  </div>
</div>
</body></html>`
    imprimirHTML(html, `limpack-cartel-${portero?.slug || 'portero'}`)
  }

  // Folleto A6 para repartir bajo puerta / portería — más chico, más punchy.
  function imprimirFolletoA6() {
    if (!qrUrlPrint) return
    const dataUrl = qrUrlPrint
    const edificio = portero?.edificio_nombre || portero?.lugar || ''
    const logoUrl = `${window.location.origin}/logo-vertical.png`
    const saludoFolleto = primerNombre
      ? `Hola vecino, soy ${esc(primerNombre)}, tu portero.`
      : 'Hola vecino. Te recomiendo Limpack.'
    const firmaFolleto = primerNombre
      ? `Cualquier cosa preguntame · <strong>${esc(primerNombre)}</strong>`
      : 'Preguntale a tu portero del edificio'
    const bulletEdificio = edificio ? `Te lo dejo yo en ${esc(edificio)}` : 'Te lo dejo yo en tu puerta'
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Folleto Limpack — ${portero?.slug || ''}</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; }
  body { font-family: 'Inter', system-ui, -apple-system, sans-serif; color: #1A3461; background: #E2E8F0; padding: 24px 0; }
  .sheet { width: 210mm; min-height: 297mm; padding: 8mm; margin: 0 auto; background: #fff; box-shadow: 0 8px 32px rgba(0,0,0,0.15); }
  .grid { display: grid; grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; gap: 4mm; height: 281mm; }
  .card { border: 1.5px dashed #CBD5E1; border-radius: 6mm; padding: 6mm 6mm 5mm; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 5mm; }
  .top { width: 100%; }
  .logo { height: 16mm; width: auto; display: block; margin: 0 auto; }
  .saludo { font-size: 12px; color: #1A3461; font-weight: 600; margin: 6mm 0 1mm; }
  .hook { font-size: 18px; font-weight: 900; color: #1A3461; margin: 2mm 0 0; line-height: 1.15; }
  .divider { width: 30%; height: 1.5px; background: #00A8B5; margin: 4mm auto 3mm; border-radius: 1px; }
  .bullets { width: 100%; max-width: 70mm; margin: 0 auto; }
  .bullet { font-size: 11px; color: #334155; line-height: 1.5; padding: 2px 0 2px 16px; position: relative; text-align: left; }
  .bullet::before { content: '✓'; color: #00A8B5; font-weight: 800; position: absolute; left: 0; top: 2px; }
  .qr-wrap { text-align: center; margin-top: auto; }
  .qr-wrap img { width: 40mm; height: 40mm; border: 3px solid #1A3461; border-radius: 4mm; background: #fff; display: inline-block; }
  .cta { font-size: 14px; font-weight: 800; color: #1A3461; margin: 3mm 0 0; }
  .firma { font-size: 10px; color: #64748B; margin-top: 2px; }
  .firma strong { color: #00A8B5; }
  .pie { font-size: 8.5px; color: #94A3B8; text-align: center; font-family: ui-monospace, monospace; margin-top: 3mm; padding-top: 2mm; border-top: 0.5px solid #E2E8F0; width: 100%; }
  @page { size: A4; margin: 0; }
  @media print {
    body { background: #fff; padding: 0; }
    .sheet { box-shadow: none; width: 210mm; min-height: 297mm; }
  }
</style></head><body>
<div class="sheet">
<div class="grid">
  ${Array(4).fill(0).map(() => `
  <div class="card">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="Limpack" />
      <div class="saludo">${saludoFolleto}</div>
      <div class="hook">Tu casa limpia, sin pensarlo.</div>
      <div class="divider"></div>
      <div class="bullets">
        <div class="bullet">Kit mensual o comprá en la tienda</div>
        <div class="bullet">Productos chequeados, buen precio</div>
        <div class="bullet">Sin contratos, cancelás cuando quieras</div>
        <div class="bullet">${bulletEdificio}</div>
      </div>
    </div>
    <div>
      <div class="qr-wrap">
        <img src="${dataUrl}" alt="QR" />
        <div class="cta">Escaneá y enterate</div>
        <div class="firma">${firmaFolleto}</div>
      </div>
      <div class="pie">${esc(refUrl)}</div>
    </div>
  </div>`).join('')}
</div>
</div>
</body></html>`
    imprimirHTML(html, `limpack-folletos-${portero?.slug || 'portero'}`)
  }

  const stats = useMemo(() => {
    const activos = vecinos.filter(v => v.estado === 'activa').length
    const pendiente = comisiones
      .filter(c => c.estado === 'pendiente' || c.estado === 'hold_verificacion')
      .reduce((s, c) => s + Number(c.monto_total || 0), 0)
    const pagado = comisiones
      .filter(c => c.estado === 'pagada')
      .reduce((s, c) => s + Number(c.monto_total || 0), 0)
    return { activos, pendiente, pagado, totalRef: vecinos.length }
  }, [vecinos, comisiones])

  if (loading) {
    return <AdminLayout title="Portero"><div style={{ padding: 40, textAlign:'center', color:'#64748B' }}>Cargando…</div></AdminLayout>
  }
  if (error || !portero) {
    return (
      <AdminLayout title="Portero">
        <div style={{ padding: 40, textAlign:'center' }}>
          <div style={{ fontSize: 16, color:'#991B1B', marginBottom: 12 }}>{error || 'Portero no encontrado'}</div>
          <Link to="/admin/porteros" style={{ color: TEAL }}>← Volver a la lista</Link>
        </div>
      </AdminLayout>
    )
  }

  const tel = portero.profile?.phone
  const telDigits = String(tel || '').replace(/\D/g,'')

  return (
    <AdminLayout title={portero.profile?.nombre || 'Portero'}>
      <div style={{ maxWidth: 1100, margin:'0 auto', padding:'0 8px' }}>

        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap: 10, margin:'4px 0 14px' }}>
          <Link to="/admin/porteros" style={{ fontSize: 13, color: TEAL, textDecoration:'none' }}>← Porteros</Link>
          <div style={{ display:'flex', gap: 6 }}>
            {tel && (
              <a href={`https://wa.me/${telDigits}`} target="_blank" rel="noreferrer" style={btnAccion(GREEN)}>
                WhatsApp
              </a>
            )}
            {portero.profile?.email && (
              <a href={`mailto:${portero.profile.email}`} style={btnAccion(NAVY)}>Email</a>
            )}
          </div>
        </div>

        {/* HEADER */}
        <div style={card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap: 14, flexWrap:'wrap' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display:'flex', gap: 8, alignItems:'center', flexWrap:'wrap', marginBottom: 4 }}>
                <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: NAVY, letterSpacing: -0.4 }}>
                  {portero.profile?.nombre || '(sin nombre)'}
                </h1>
                <Pill activo={portero.activo}>{portero.activo ? 'ACTIVO' : 'INACTIVO'}</Pill>
                {portero.tyc_aceptado_at && (
                  <span style={{ ...pill, background:'rgba(0,168,181,.1)', color:'#0E7A82' }}>T&C v{portero.tyc_version || '?'} ✓</span>
                )}
              </div>
              <div style={{ fontSize: 14, color:'#334155' }}>
                <strong>{portero.edificio_nombre || portero.lugar || '(sin edificio)'}</strong>
                {portero.direccion && ` · ${portero.direccion}`}
                {portero.barrio?.nombre && ` · ${portero.barrio.nombre}`}
              </div>
              <div style={{ fontSize: 13, color:'#64748B', marginTop: 4 }}>
                {portero.cant_unidades ? `${portero.cant_unidades} unidades` : '— unidades'}
                {portero.turno && ` · turno ${portero.turno}`}
                {tel && ` · ☎ ${tel}`}
                {portero.profile?.email && ` · ${portero.profile.email}`}
              </div>
              <div style={{ fontSize: 12, color:'#94A3B8', marginTop: 4 }}>
                Alta: {fmtFecha(portero.created_at)} · slug: <code style={{ background:'#F1F5F9', padding:'1px 5px', borderRadius: 3 }}>{portero.slug}</code>
              </div>
            </div>
          </div>
        </div>

        {/* KPIs */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginTop: 10 }}>
          <Kpi label="Vecinos referidos"  value={stats.totalRef}                       />
          <Kpi label="Activos hoy"        value={stats.activos}    color={GREEN}        />
          <Kpi label="Comisión pendiente" value={fmtUYU(stats.pendiente)} color="#92400E" />
          <Kpi label="Total pagado"       value={fmtUYU(stats.pagado)}    color={NAVY}    />
        </div>

        {/* MATERIAL DE DIFUSIÓN — QR + volantes + share */}
        <div style={{ ...card, marginTop: 14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: 10, gap: 8, flexWrap:'wrap' }}>
            <h2 style={{ ...h2, margin: 0 }}>Material de difusión</h2>
            {!portero.slug && (
              <span style={{ fontSize: 11.5, color:'#92400E', background:'rgba(245,158,11,.12)', padding:'2px 8px', borderRadius: 3, fontWeight: 700 }}>
                Sin slug — no se puede generar
              </span>
            )}
          </div>

          {portero.slug ? (
            <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', gap: 16, alignItems:'flex-start', flexWrap:'wrap' }}>
              {/* QR */}
              <div style={{ textAlign:'center' }}>
                <div style={{ display:'inline-block', padding: 8, background:'#fff', border:'1px solid #E2E8F0', borderRadius: 8, minWidth: 240, minHeight: 240, position:'relative' }}>
                  {qrUrlDisplay && (
                    <img
                      src={qrUrlDisplay}
                      alt={`QR ${portero.slug}`}
                      width={220} height={220}
                      style={{ display:'block', opacity: qrReady ? 1 : 0, transition: 'opacity 120ms' }}
                      onLoad={() => setQrReady(true)}
                      onError={() => { setQrReady(false); alert('No se pudo cargar el QR. Chequeá tu conexión y reintentá.') }}
                    />
                  )}
                  {!qrReady && (
                    <div style={{ position:'absolute', inset: 0, display:'flex', alignItems:'center', justifyContent:'center', color:'#94A3B8', fontSize: 12 }}>
                      Generando QR…
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 11, color:'#64748B', marginTop: 6, fontFamily:'ui-monospace, monospace' }}>
                  /{portero.slug}
                </div>
              </div>

              {/* Acciones */}
              <div style={{ minWidth: 240, flex: 1 }}>
                <div style={{ fontSize: 12, color:'#64748B', marginBottom: 4, fontWeight: 600, letterSpacing: 0.2 }}>LINK PERSONAL</div>
                <div onClick={copiarLink} title="Click para copiar"
                     style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 8, padding:'8px 10px', background:'#F8FAFC', border:'0.5px solid #E2E8F0', borderRadius: 6, cursor:'pointer', marginBottom: 10 }}>
                  <code style={{ fontSize: 12, color: NAVY, fontFamily:'ui-monospace, monospace', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{refUrl}</code>
                  <span style={{ fontSize: 11, color: TEAL, fontWeight: 700, flexShrink: 0 }}>{copiado ? '✓ Copiado' : '📋 Copiar'}</span>
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                  <button onClick={imprimirCartelA4} disabled={!qrReady}
                    style={{ ...btnPrim, opacity: qrReady ? 1 : 0.5, cursor: qrReady ? 'pointer' : 'wait' }}>
                    🖨️ Cartel A4
                  </button>
                  <button onClick={imprimirFolletoA6} disabled={!qrReady}
                    style={{ ...btnPrim, opacity: qrReady ? 1 : 0.5, cursor: qrReady ? 'pointer' : 'wait' }}>
                    🖨️ Folletos (4 por A4)
                  </button>
                  <button onClick={descargarQR} disabled={!qrReady}
                    style={{ ...btnSec, opacity: qrReady ? 1 : 0.5, cursor: qrReady ? 'pointer' : 'wait' }}>
                    ⬇️ Descargar QR
                  </button>
                  <button onClick={compartirPorWA}
                    style={{ ...btnSec, color:'#25D366', borderColor:'rgba(37,211,102,.4)' }}>
                    💬 Enviar al portero
                  </button>
                </div>

                <div style={{ fontSize: 11.5, color:'#64748B', marginTop: 10, lineHeight: 1.5 }}>
                  El <strong>Cartel A4</strong> es un QR grande para colgar en el palier. Los <strong>folletos</strong> imprimen 4 por A4 para cortar y repartir.
                  Cada uno abre <em>una pestaña preview</em> + dispara el diálogo de impresión. Si el navegador bloquea pop-ups, te descarga el HTML como archivo.
                  <strong>Enviar al portero</strong> abre WhatsApp con link + texto pre-armado{portero.profile?.phone ? '' : ' (sin teléfono cargado, abre selector)'}.
                </div>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color:'#94A3B8', fontStyle:'italic', padding:'12px 0' }}>
              Este portero no tiene <code>slug</code> asignado. Cargáselo en la BD para poder generar el QR y el material.
            </div>
          )}
        </div>

        {/* LIQUIDACIÓN / BANCO */}
        <div style={{ ...card, marginTop: 14 }}>
          <h2 style={h2}>Datos para liquidar</h2>
          {(portero.banco_cuenta || portero.notas_pago) ? (
            <div style={{ fontSize: 13.5, color:'#334155', lineHeight: 1.6 }}>
              {portero.banco_cuenta && (
                <div><strong>Cuenta:</strong> <span style={{ fontFamily:'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{portero.banco_cuenta}</span></div>
              )}
              {portero.notas_pago && (
                <div style={{ marginTop: 4 }}><strong>Notas:</strong> {portero.notas_pago}</div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: 13, color:'#94A3B8', fontStyle:'italic' }}>
              Sin cuenta cargada. El portero la completa desde su dashboard <code>/portero</code>.
            </div>
          )}
        </div>

        {/* VECINOS */}
        <div style={{ ...card, marginTop: 14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 10 }}>
            <h2 style={{ ...h2, margin: 0 }}>Vecinos referidos ({vecinos.length})</h2>
            {portero.slug && (
              <a href={`https://limpack.uy/p/${portero.slug}`} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: TEAL, textDecoration:'none' }}>
                Ver link público ↗
              </a>
            )}
          </div>
          {vecinos.length === 0 ? (
            <div style={{ padding: 20, textAlign:'center', color:'#94A3B8', fontSize: 13 }}>
              Sin vecinos referidos todavía.
            </div>
          ) : (
            <div style={{ display:'grid', gap: 8 }}>
              {vecinos.map(v => <VecinoRow key={v.id} v={v} />)}
            </div>
          )}
        </div>

        {/* COMISIONES */}
        <div style={{ ...card, marginTop: 14, marginBottom: 20 }}>
          <h2 style={h2}>Historial de comisiones</h2>
          {comisiones.length === 0 ? (
            <div style={{ padding: 20, textAlign:'center', color:'#94A3B8', fontSize: 13 }}>
              Sin comisiones generadas todavía.
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom:'0.5px solid #E2E8F0', color:'#64748B', textAlign:'left' }}>
                    <th style={th}>Mes</th>
                    <th style={th}>Clientes</th>
                    <th style={{ ...th, textAlign:'right' }}>Monto</th>
                    <th style={th}>Estado</th>
                    <th style={th}>Pago</th>
                    <th style={th}>Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {comisiones.map(c => {
                    const est = ESTADO_COMISION[c.estado] || { bg:'#F1F5F9', color:'#64748B', label: c.estado }
                    return (
                      <tr key={c.id} style={{ borderBottom:'0.5px solid #F1F5F9' }}>
                        <td style={{ ...td, fontWeight: 600, color: NAVY, textTransform:'capitalize' }}>{fmtMes(c.mes)}</td>
                        <td style={td}>{c.cantidad_clientes}</td>
                        <td style={{ ...td, textAlign:'right', fontWeight: 600 }}>{fmtUYU(c.monto_total)}</td>
                        <td style={td}>
                          <span style={{ ...pill, background: est.bg, color: est.color }}>{est.label}</span>
                          {c.estado === 'hold_verificacion' && c.hold_motivo && (
                            <div style={{ fontSize: 11, color:'#64748B', marginTop: 2 }}>{c.hold_motivo}</div>
                          )}
                        </td>
                        <td style={td}>
                          {c.fecha_pago ? (
                            <>
                              <div>{fmtFecha(c.fecha_pago)}</div>
                              {c.referencia_pago && <div style={{ fontSize: 11, color:'#64748B', fontFamily:'ui-monospace, monospace' }}>{c.referencia_pago}</div>}
                            </>
                          ) : '—'}
                        </td>
                        <td style={{ ...td, color:'#64748B', fontSize: 12 }}>{c.notas || ''}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </AdminLayout>
  )
}

function VecinoRow({ v }) {
  const est = ESTADO_VECINO[v.estado] || { bg:'#F1F5F9', color:'#64748B', label: v.estado }
  const titularTel = v.titular?.phone
  return (
    <div style={{ display:'flex', justifyContent:'space-between', gap: 10, padding:'10px 12px', background:'#F8FAFC', borderRadius: 8, alignItems:'flex-start', flexWrap:'wrap' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
          <Link to={`/admin/usuarios/${v.titular?.id}`} target="_blank" style={{ fontWeight: 600, color: NAVY, textDecoration:'none', fontSize: 14 }}>
            {v.titular?.nombre || '(sin nombre)'}
          </Link>
          <span style={{ ...pill, background: est.bg, color: est.color }}>{est.label}</span>
          {v.kit_actual && (
            <span style={{ fontSize: 11.5, color:'#475569', background:'#fff', padding:'2px 6px', borderRadius: 3, border:'0.5px solid #E2E8F0' }}>
              {v.kit_actual}
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color:'#64748B', marginTop: 3 }}>
          {v.direccion || '—'}{v.apto ? ` apto ${v.apto}` : ''}
          {v.dia_entrega && ` · entrega ${v.dia_entrega}`}
          {v.ventana_horaria && ` ${v.ventana_horaria}`}
        </div>
        <div style={{ fontSize: 11.5, color:'#94A3B8', marginTop: 2 }}>
          Inicio: {fmtFecha(v.fecha_inicio)}
          {v.fecha_proximo_cobro && ` · Próx. cobro: ${fmtFecha(v.fecha_proximo_cobro)}`}
        </div>
      </div>
      {titularTel && (
        <a href={`https://wa.me/${String(titularTel).replace(/\D/g,'')}`} target="_blank" rel="noreferrer"
           style={{ fontSize: 11.5, padding:'4px 8px', border:'0.5px solid rgba(37,211,102,.4)', borderRadius: 4, color:'#25D366', textDecoration:'none', fontWeight: 600, background:'#fff' }}>
          WhatsApp
        </a>
      )}
    </div>
  )
}

function Kpi({ label, value, color = NAVY }) {
  return (
    <div style={{ background:'#fff', border:'0.5px solid #E2E8F0', borderRadius: 8, padding:'10px 12px' }}>
      <div style={{ fontSize: 11, color:'#64748B', fontWeight: 600, letterSpacing: 0.3, textTransform:'uppercase' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 2, lineHeight: 1.1 }}>{value}</div>
    </div>
  )
}

function Pill({ activo, children }) {
  return (
    <span style={{
      fontSize: 10.5, padding:'2px 7px', borderRadius: 3, fontWeight: 700,
      background: activo ? 'rgba(22,163,74,.1)' : 'rgba(100,116,139,.15)',
      color: activo ? GREEN : '#475569',
    }}>{children}</span>
  )
}

const card = { background:'#fff', border:'0.5px solid #E2E8F0', borderRadius: 10, padding: 14 }
const h2 = { margin:'0 0 10px', fontSize: 14, fontWeight: 700, color: NAVY, letterSpacing: 0.3, textTransform:'uppercase' }
const pill = { fontSize: 10.5, padding:'2px 7px', borderRadius: 3, fontWeight: 700 }
const th = { padding:'8px 8px', fontWeight: 600, fontSize: 11, textTransform:'uppercase', letterSpacing: 0.3 }
const td = { padding:'10px 8px', verticalAlign:'top' }

const btnAccion = (color) => ({
  fontSize: 12, padding:'6px 12px',
  border:`0.5px solid ${color}`, borderRadius: 6,
  color, textDecoration:'none', fontWeight: 600, background:'#fff',
})

const btnPrim = {
  fontSize: 13, padding:'10px 14px',
  border:'none', borderRadius: 6,
  background: NAVY, color:'#fff', fontWeight: 700,
  cursor:'pointer', fontFamily:'inherit',
}
const btnSec = {
  fontSize: 13, padding:'10px 14px',
  border:'0.5px solid #CBD5E1', borderRadius: 6,
  background:'#fff', color: NAVY, fontWeight: 600,
  cursor:'pointer', fontFamily:'inherit',
}
